// Storage helpers for private Supabase buckets.
//
// The buckets were public for the app's first months — DB columns like
// scale_photo_url / truck_photo_url store the full publicUrl. Once the
// buckets went private (migration 048) those URLs return 401. This helper
// accepts either format and produces a signed URL on demand.

import { useQuery } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { decodeJwtPayload } from '@/lib/jwt'
import { friendlyError } from '@/lib/friendlyError'

const DEFAULT_TTL_SECONDS = 60 * 60   // 1 hour — short enough to be safe, long
                                       //         enough that a tab open all day
                                       //         only re-fetches once.

// ---------------------------------------------------------------------------
// storageFrom — THE single seam for object-storage access. Every storage
// read/write in the app routes through this module: either this low-level
// accessor or the higher-level helpers below (uploadWithTimeout / signedUrl /
// signedUrlBatch). Nothing else should call `client.storage.from(...)` directly.
// Swapping the storage backend (self-hosted Supabase Storage, or an S3/R2
// adapter) then means reimplementing this one file, not touching call sites.
// Pass an isolated client (saveDb) as `db` to keep a write off a busy page's
// auth lock. See docs/SUPABASE-EXIT-PLAN.md.
// ---------------------------------------------------------------------------
export function storageFrom(bucket: string, db: typeof supabase = supabase) {
  return db.storage.from(bucket)
}

// ---------------------------------------------------------------------------
// compressImage — resize + re-encode a camera photo to a Blob before upload.
// Tablet cameras produce 4-8 MB JPEGs; uploading those over flaky packhouse
// Wi-Fi is the slowest single thing operators do. compressStandard cuts the
// payload to ~200-400 KB without visible loss for scale photos / signatures
// / reject documentation. compressQc is gentler so fruit-defect detail stays
// inspectable.
//
// "maxDimension" is the longest side of the image — works for both landscape
// and portrait without distortion.
// ---------------------------------------------------------------------------
export interface CompressOptions {
  maxDimension?: number   // max longest side in px; default 1280
  quality?:      number   // 0..1; default 0.78
  mime?:         string   // default 'image/jpeg'
}

// Note on EXIF: drawing through a canvas and re-encoding via toBlob
// strips ALL metadata (EXIF, GPS, camera serial, etc.) as a side effect.
// We rely on that for privacy hygiene — operators shouldn't be uploading
// their phone's GPS coordinates or device fingerprint to public-by-URL
// photos. If we ever switch to a no-recompress fast path, add explicit
// EXIF scrubbing back.
//
// Fast path: createImageBitmap() decodes off the main thread on modern
// browsers (Chrome 50+, Safari 15+, Firefox 42+), which is the slowest
// step for 6-8 MB camera JPEGs on phones. Falls back to the new Image()
// + ObjectURL path on older WebViews. Either way the canvas write +
// toBlob still runs on the main thread, but the decode is the part that
// dominates 1-2s per photo on a packhouse tablet.
export async function compressImage(file: Blob, opts: CompressOptions = {}): Promise<Blob> {
  const maxDimension = opts.maxDimension ?? 1280
  const quality      = opts.quality      ?? 0.78
  const mime         = opts.mime         ?? 'image/jpeg'

  // Try the fast path first — createImageBitmap with `imageOrientation:
  // 'from-image'` honours the EXIF orientation flag so portrait photos
  // don't come out sideways. Falls back to the legacy Image() decoder if
  // the API is unavailable or rejects (some older Safari versions throw
  // on certain JPEGs).
  let width: number, height: number
  let source: CanvasImageSource
  try {
    if (typeof createImageBitmap === 'function') {
      const bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' })
      width  = bitmap.width
      height = bitmap.height
      source = bitmap
    } else {
      throw new Error('createImageBitmap unavailable')
    }
  } catch {
    const { img } = await decodeViaImageElement(file)
    width  = img.naturalWidth
    height = img.naturalHeight
    source = img
  }

  const longest = Math.max(width, height)
  const scale = longest > maxDimension ? maxDimension / longest : 1
  const canvas = document.createElement('canvas')
  canvas.width  = Math.round(width  * scale)
  canvas.height = Math.round(height * scale)
  const ctx = canvas.getContext('2d')
  if (!ctx) throw new Error('canvas 2d context unavailable')
  ctx.drawImage(source, 0, 0, canvas.width, canvas.height)
  // Release the bitmap GPU memory eagerly on the fast path. The legacy
  // Image() path's URL.revokeObjectURL is handled inside decodeViaImageElement.
  if ('close' in source && typeof (source as ImageBitmap).close === 'function') {
    (source as ImageBitmap).close()
  }

  const canvasBlob = await new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      blob => blob ? resolve(blob) : reject(new Error('compressImage: toBlob returned null')),
      mime, quality,
    )
  })

  // Force the blob into an in-memory ArrayBuffer-backed body before
  // returning it. Without this step, canvas.toBlob() on some Android
  // Chrome builds (notably Samsung Tab S9 FE) returns a Blob backed
  // by an opaque streaming source that fetch() reads at a few KB/s —
  // a 100 KB photo upload that should take ~500 ms hangs for 30-45 s
  // and trips uploadWithTimeout. The same bytes wrapped in a fresh
  // Blob([ArrayBuffer]) upload normally. Cheap (~10ms for a 300 KB
  // photo on the slowest device we test), and a no-op on browsers
  // that don't have the bug.
  const buf = await canvasBlob.arrayBuffer()
  return new Blob([buf], { type: canvasBlob.type || mime })
}

function decodeViaImageElement(file: Blob): Promise<{ img: HTMLImageElement }> {
  return new Promise((resolve, reject) => {
    const img = new Image()
    const url = URL.createObjectURL(file)
    img.onload  = () => { URL.revokeObjectURL(url); resolve({ img }) }
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('compressImage: image failed to decode')) }
    img.src = url
  })
}

// Standard preset — truck inspection, scale, delivery note, reject log,
// rebin. Aggressive enough to flatten 6 MB camera output to under 400 KB
// without losing legibility on text / numbers in the frame.
export const compressStandard = (file: Blob) =>
  compressImage(file, { maxDimension: 1280, quality: 0.78 })

// QC preset — used to be 2000px × 0.88 ("retain defect detail for desktop
// review") but that produced ~1.5 MB files that took multiple minutes
// each to upload over the packhouse Wi-Fi. Operators almost always
// review on the floor tablet which is 1280px wide, so the extra
// resolution was wasted bytes. Now matches compressStandard — 1280px
// × 0.78 (~350 KB) — for ~4× faster uploads.
export const compressQc = compressStandard

// safeCompress — replaces the old pattern of
// `compressStandard(file).catch(() => file)`, which silently shipped
// the raw 5-10 MB camera JPEG when compression threw (memory pressure,
// canvas size limit on older iPads, unsupported HEIC, etc.). A single
// silent-raw upload could take 5-10 minutes on packhouse Wi-Fi.
//
// Strategy: try standard 1280px × 0.78 first; on failure, retry at
// 800px × 0.7 (smaller canvas = less memory pressure, smaller output
// still legible for compliance evidence). If both fail, throw — the
// caller surfaces "couldn't process photo" to the operator. The raw
// file is never uploaded.
export async function safeCompress(file: Blob): Promise<Blob> {
  try {
    return await compressStandard(file)
  } catch (firstErr) {
    try {
      return await compressImage(file, { maxDimension: 800, quality: 0.7 })
    } catch (secondErr) {
      throw new Error(
        `Couldn't process photo. Try a different photo. ` +
        `(${(secondErr as Error).message || (firstErr as Error).message})`,
      )
    }
  }
}

// ---------------------------------------------------------------------------
// uploadWithTimeout — supabase.storage.upload() has no client-side timeout,
// so a Wi-Fi blip mid-upload can leave the request hanging for minutes (the
// browser only kills the underlying socket after its own long fallback —
// observed at 5-30 min in the packhouse). When a form like QC does
// Promise.all() over several photo uploads, a single stall blocks the
// whole save. This wrapper races the upload against a setTimeout reject
// so we surface a clear error to the operator and let them retry.
//
// Note: this doesn't cancel the underlying fetch — supabase-js v2 does not
// accept an AbortSignal for storage uploads. The background request may
// eventually succeed or fail naturally; that's fine, we just stop blocking
// the operator on it.
//
// Default 45s gives a single 1.5 MB photo plenty of room on a slow 3G
// connection (~50 KB/s = 30s) while still surfacing a clearly-stuck upload
// before the operator gives up.
// ---------------------------------------------------------------------------
const UPLOAD_TIMEOUT_MS = 45_000

// ---------------------------------------------------------------------------
// tenantPath — prepend the active tenant_id to a storage object path.
//
// Why this exists: storage buckets are shared across tenants by design (one
// bucket per kind of artefact), but the existing RLS policies only check
// `bucket_id = '...'` — any authed user from any tenant can download any
// other tenant's photos / docs once they know the object path. Council bug
// sweep T1.1.
//
// Forward-only fix: NEW uploads go to `<tenant_id>/<original_path>`. A
// future migration will tighten storage.objects RLS to require that prefix
// matches `current_tenant_id()`. Legacy paths (no UUID prefix) stay
// readable through the transitional policy until they age out / are
// re-uploaded.
//
// Throws if `tenantId` is null — caller MUST resolve the active tenant
// before constructing a path. The caller is typically inside a React mount
// that already has `useActiveTenant()` data; if not, surfacing the error
// is much better than silently writing an untenanted object.
// ---------------------------------------------------------------------------
export function tenantPath(tenantId: string | null, path: string): string {
  if (!tenantId) {
    throw new Error('tenantPath: active tenant is unknown — refresh the page and try again')
  }
  // Defensive trim so a leading slash on the path doesn't produce
  // `<tenant>//rest`, which Postgres' storage layer treats as two folder
  // segments. We also reject already-prefixed paths to make double-calls a
  // no-op rather than `<tenant>/<tenant>/rest`.
  const clean = path.replace(/^\/+/, '')
  if (clean.startsWith(`${tenantId}/`)) return clean
  return `${tenantId}/${clean}`
}

// resolveActiveTenantId — non-hook read of the active_tenant_id JWT claim.
//
// offlineQueue.ts and any other code outside a React component can't call
// useActiveTenant(). This decodes the same claim useActiveTenant reads, so a
// queued upload can be tenant-prefixed at drain time. Returns null when there's
// no session / no claim (e.g. a platform-admin tenant that doesn't get the
// claim, mig 241) — the caller decides whether to require it.
export async function resolveActiveTenantId(): Promise<string | null> {
  try {
    const { data } = await supabase.auth.getSession()
    const token = data.session?.access_token
    if (!token) return null
    const payload = decodeJwtPayload(token)
    return (payload?.active_tenant_id as string | undefined) ?? null
  } catch {
    return null
  }
}

// tenantPathResolved — convenience for non-hook callers: resolve the tenant
// then prefix. Throws (via tenantPath) if the tenant can't be resolved, so an
// untenanted object is never written.
export async function tenantPathResolved(path: string): Promise<string> {
  const tenantId = await resolveActiveTenantId()
  return tenantPath(tenantId, path)
}

export async function uploadWithTimeout(
  bucket: string,
  path: string,
  blob: Blob,
  opts: { upsert?: boolean; contentType?: string; cacheControl?: string } = {},
  timeoutMs: number = UPLOAD_TIMEOUT_MS,
  // Optional override of the supabase client used for the upload. Pass
  // `saveDb` from useSaveDb() when calling from inside a busy mutation
  // so the storage call uses the lock-free isolated client (same QC
  // pattern, see useSaveDb.ts).
  client: { storage: typeof supabase.storage } = supabase,
): Promise<{ data: { path: string } | null; error: Error | null }> {
  const upload = client.storage.from(bucket).upload(path, blob, opts)
  let timeoutId: ReturnType<typeof setTimeout> | undefined
  try {
    const result = await Promise.race([
      upload.then(r => ({ kind: 'done' as const, r })),
      new Promise<{ kind: 'timeout' }>(resolve => {
        timeoutId = setTimeout(() => resolve({ kind: 'timeout' }), timeoutMs)
      }),
    ])
    if (result.kind === 'timeout') {
      const kind = opts.contentType?.startsWith('application/pdf') ? 'PDF upload'
                 : opts.contentType?.startsWith('image/')          ? 'Photo upload'
                 :                                                   'Upload'
      return {
        data: null,
        error: new Error(
          `${kind} is taking longer than ${Math.round(timeoutMs / 1000)}s — your connection looks slow. ` +
          `It may still finish in the background; check the document list before re-uploading, and don't refresh the page.`,
        ),
      }
    }
    return result.r as { data: { path: string } | null; error: Error | null }
  } finally {
    if (timeoutId) clearTimeout(timeoutId)
  }
}

/**
 * True when a storage upload was refused because an object ALREADY sits at
 * that path (Storage answers 409 "The resource already exists" to an
 * `upsert: false` upload). For a path keyed on a client-minted id — the offline
 * drain's scouting photos — that object IS this photo from an earlier attempt,
 * so the upload has in effect succeeded. Lets those uploads stop overwriting
 * (`upsert: false`): since mig 20261037700000 only a file's uploader or a
 * manager may overwrite a scouting photo, and a retry under another sign-in
 * must not lose the photo over it.
 */
export function isAlreadyUploaded(err: unknown): boolean {
  if (!err || typeof err !== 'object') return false
  const e = err as { statusCode?: unknown; status?: unknown; error?: unknown; message?: unknown }
  return e.statusCode === '409' || e.status === 409 || e.error === 'Duplicate'
      || (typeof e.message === 'string' && /already exists/i.test(e.message))
}

/**
 * The Error to throw when an upload fails: your sentence, then friendlyError's
 * operator sentence for the storage error. `${err.message}` put the raw
 * storage text on screen ("new row violates row-level security policy"), and
 * friendlyError on the wrapped text then swapped the whole message, your
 * "nothing was saved" included, for its generic permission sentence. A plain
 * Error, so it still reaches the reporter; the raw error rides on `cause`.
 */
export function uploadFailed(lead: string, err: unknown): Error {
  return new Error(`${lead} ${friendlyError(err).message}`, { cause: err })
}

// ---------------------------------------------------------------------------
// withRequestTimeout — generic Promise.race wrapper used to guard the
// supabase-js insert/update/upsert calls in long-running mutations. Same
// rationale as uploadWithTimeout: without a client-side cap, a stalled
// request waits on the browser's default socket timeout (minutes).
// ---------------------------------------------------------------------------
const DB_TIMEOUT_MS = 30_000

export async function withRequestTimeout<T>(
  p: PromiseLike<T>,
  label: string = 'Request',
  timeoutMs: number = DB_TIMEOUT_MS,
): Promise<T> {
  let timeoutId: ReturnType<typeof setTimeout> | undefined
  try {
    return await Promise.race([
      Promise.resolve(p),
      new Promise<T>((_resolve, reject) => {
        timeoutId = setTimeout(
          () => reject(new Error(`${label} timed out after ${Math.round(timeoutMs / 1000)}s — check your connection.`)),
          timeoutMs,
        )
      }),
    ])
  } finally {
    if (timeoutId) clearTimeout(timeoutId)
  }
}

// ---------------------------------------------------------------------------
// dataUrlToBlob — decode a data: URI into a Blob without using fetch().
// We can't use fetch(dataUrl) because the production CSP omits `data:` from
// connect-src (security: prevents exfiltration via fetch), and Chrome reports
// the resulting CSP block as a generic "TypeError: Failed to fetch".
// ---------------------------------------------------------------------------
export function dataUrlToBlob(dataUrl: string): Blob {
  const comma = dataUrl.indexOf(',')
  if (!dataUrl.startsWith('data:') || comma < 0) {
    throw new Error('Not a data: URL')
  }
  const header = dataUrl.slice(5, comma)
  const body   = dataUrl.slice(comma + 1)
  const isBase64 = /;base64$/i.test(header)
  const mime = (isBase64 ? header.replace(/;base64$/i, '') : header).split(';')[0] || 'application/octet-stream'
  const binary = isBase64 ? atob(body) : decodeURIComponent(body)
  const bytes = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i)
  return new Blob([bytes], { type: mime })
}

// ---------------------------------------------------------------------------
// blobToBase64 — encode a Blob as a bare base64 string (no `data:` prefix).
// The inverse of dataUrlToBlob. Used to hand a compressed receipt photo to the
// extract-receipt edge function (Claude vision takes base64 image input).
// FileReader decodes off the main thread; we slice off the `data:...;base64,`
// header so callers get the payload the Anthropic image source expects.
// ---------------------------------------------------------------------------
export function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => {
      const res = typeof reader.result === 'string' ? reader.result : ''
      const comma = res.indexOf(',')
      resolve(comma >= 0 ? res.slice(comma + 1) : res)
    }
    reader.onerror = () => reject(new Error('blobToBase64: failed to read blob'))
    reader.readAsDataURL(blob)
  })
}

// ---------------------------------------------------------------------------
// extractPath — turn a stored value (path OR legacy public URL) into the
// clean object path inside the bucket. Returns null if the value isn't
// recognisable for the given bucket.
// ---------------------------------------------------------------------------
export function extractPath(bucket: string, value: string | null | undefined): string | null {
  if (!value) return null
  const v = value.trim()
  if (!v) return null

  // Already a clean path — no scheme, no /storage/ prefix.
  if (!v.startsWith('http://') && !v.startsWith('https://') && !v.startsWith('/')) {
    return v
  }

  // Public URL: ".../storage/v1/object/public/{bucket}/<path>"
  // Or signed URL: ".../storage/v1/object/sign/{bucket}/<path>?token=…"
  const re = new RegExp(`/storage/v1/object/(?:public|sign)/${bucket}/([^?]+)`)
  const m = v.match(re)
  if (m && m[1]) {
    try {
      return decodeURIComponent(m[1])
    } catch {
      return m[1]
    }
  }

  // Couldn't parse — return null so callers fall back to a placeholder.
  return null
}

// ---------------------------------------------------------------------------
// signedUrl — one-shot, no caching. Returns null on failure.
// ---------------------------------------------------------------------------
export async function signedUrl(bucket: string, value: string | null | undefined, ttl = DEFAULT_TTL_SECONDS): Promise<string | null> {
  const path = extractPath(bucket, value)
  if (!path) return null
  const { data, error } = await supabase.storage.from(bucket).createSignedUrl(path, ttl)
  if (error) return null
  return data?.signedUrl ?? null
}

// ---------------------------------------------------------------------------
// useSignedUrl — react-query hook for inline images. Re-fetches just before
// the URL would expire (we set staleTime to 90% of the TTL).
// ---------------------------------------------------------------------------
export function useSignedUrl(bucket: string, value: string | null | undefined, ttl = DEFAULT_TTL_SECONDS) {
  const path = extractPath(bucket, value)
  return useQuery({
    queryKey: ['signed-url', bucket, path],
    enabled: !!path,
    staleTime: Math.floor(ttl * 0.9 * 1_000),
    queryFn: async () => {
      const url = await signedUrl(bucket, path, ttl)
      return url
    },
  })
}

// ---------------------------------------------------------------------------
// signedUrlBatch — sign many paths in a single round trip. Useful for grids
// of evidence photos. Returns an object keyed by the original input value.
// ---------------------------------------------------------------------------
export async function signedUrlBatch(bucket: string, values: (string | null | undefined)[], ttl = DEFAULT_TTL_SECONDS): Promise<Record<string, string | null>> {
  const out: Record<string, string | null> = {}
  const paths: string[] = []
  const valueByPath: Record<string, string> = {}

  for (const v of values) {
    if (!v) continue
    const p = extractPath(bucket, v)
    if (p) {
      paths.push(p)
      valueByPath[p] = v
    } else {
      out[v] = null
    }
  }

  if (paths.length === 0) return out

  const { data, error } = await supabase.storage.from(bucket).createSignedUrls(paths, ttl)
  if (error || !data) {
    for (const p of paths) out[valueByPath[p]] = null
    return out
  }
  for (const row of data) {
    const v = valueByPath[row.path ?? '']
    if (v) out[v] = row.signedUrl ?? null
  }
  return out
}
